// Resume (pay-now spec §7.5): the full-page checkout survives a reload and
// the Paystack round trip in the same tab (the drawer keeps the same entry,
// only as it leaves for Paystack, so a guest Paystack did not charge comes
// back to Review & pay). sessionStorage holds ONLY the cart
// token, the booking ids, the lines as the cart shows them (service, option,
// start, party, price), where the guest was (step, rider), the last trip to
// Paystack (its payment reference), whether a code was applied and of which
// kind (never the code), the marketing tick, and whether Special requests or
// a Country were given (never what): never an answer and never the booker's
// contact. Answers the guest saved come back from the server's reader; the
// booker's details from the guest profile, as always; after a trip to
// Paystack, Special requests and Country from the unpaid payment
// (paystack-payment-status). Every access is in try/catch: private windows,
// blocked storage and previews work without it (the checkout simply starts
// afresh).
import { extendCartHold, getCartHoldState } from "@vertu/shared";
import { supabase } from "../../config";
import type { CartItem } from "../../state/cart";
import { adoptCartToken } from "../../state/booking";
import {
  buildResume,
  markNotPaid,
  parseResume,
  resumeKey,
  type CheckoutResume,
  type CodeKind,
  type ReopenedFields,
  type ResumeGiven,
  type ResumePayment,
  type StepId,
} from "../../state/checkout-steps";
import {
  buildHop,
  hopKey,
  markEntryReturned,
  markHopReturned,
  markHopSaid,
  parseHop,
  type PaystackHopRecord,
  type PaystackSaid,
} from "../../state/payment-return";

export function readCheckoutResume(tenantSlug: string, now = Date.now()): CheckoutResume | null {
  try {
    return parseResume(window.sessionStorage.getItem(resumeKey(tenantSlug)), now);
  } catch {
    return null;
  }
}

export function clearCheckoutResume(tenantSlug: string): void {
  try {
    window.sessionStorage.removeItem(resumeKey(tenantSlug));
  } catch {
    /* storage blocked: nothing was kept */
  }
}

/**
 * Keep where the guest is. A cart that cannot be resumed faithfully (a spa
 * treatment, a co-hosted pairing, a line with add-ons: buildResume says null)
 * keeps nothing.
 */
export function saveCheckoutResume(
  tenantSlug: string,
  input: {
    cartToken: string;
    items: readonly CartItem[];
    step: StepId;
    riderIndex: number;
    payment?: ResumePayment | null;
    codesApplied?: boolean;
    codeKinds?: readonly CodeKind[];
    linkCodeOnly?: boolean;
    marketing?: boolean | null;
    given?: Partial<ResumeGiven>;
  },
): void {
  const entry = buildResume({ ...input, now: Date.now() });
  try {
    if (!entry) window.sessionStorage.removeItem(resumeKey(tenantSlug));
    else window.sessionStorage.setItem(resumeKey(tenantSlug), JSON.stringify(entry));
  } catch {
    /* storage blocked: the checkout works without it */
  }
}

/** Paystack said the kept checkout's trip was not paid, and it reopened: a later load asks again quietly. */
export function markCheckoutNotPaid(tenantSlug: string, reference: string): void {
  const entry = readCheckoutResume(tenantSlug);
  if (!entry) return;
  const marked = markNotPaid(entry, reference);
  if (marked === entry) return;
  try {
    window.sessionStorage.setItem(resumeKey(tenantSlug), JSON.stringify(marked));
  } catch {
    /* storage blocked */
  }
}

/**
 * Has every hold of the kept checkout ended (get_cart_hold_state: all
 * lapsed)? Read-only, so asking never keeps a hold alive. False when any
 * hold is still held, went on (confirmed, cancelled: gone), or the server
 * did not answer.
 */
export async function keptHoldsEnded(entry: CheckoutResume): Promise<boolean> {
  const state = await getCartHoldState(supabase, entry.cartToken, entry.bookingIds).catch(() => null);
  if (!state || !state.ok || state.holds.length === 0) return false;
  return state.holds.every((h) => h.state === "lapsed");
}

/**
 * Paystack sent the browser back with this reference (or the browser's own
 * Back came from its page): the kept checkout remembers it, so a reload from
 * here asks about THIS payment again instead of reopening Pay. Only its own
 * trip (markEntryReturned): another reference leaves the entry as it is.
 */
export function markCheckoutReturned(tenantSlug: string, reference: string): void {
  const entry = readCheckoutResume(tenantSlug);
  if (!entry) return;
  const marked = markEntryReturned(entry, reference, Date.now());
  if (marked === entry) return;
  try {
    window.sessionStorage.setItem(resumeKey(tenantSlug), JSON.stringify(marked));
  } catch {
    /* storage blocked */
  }
}

// ── The trip to Paystack (state/payment-return.ts: hopKey) ──────────────────
// The payment's reference and the cart token that proves this tab owns it,
// kept for every checkout that leaves for Paystack (drawer or page, and the
// carts the resume entry cannot keep), so the return can ask the check at
// once. Nothing else.

export function keepPaystackHop(tenantSlug: string, reference: string, cartToken: string): void {
  const hop = buildHop(reference, cartToken, Date.now());
  try {
    if (hop) window.sessionStorage.setItem(hopKey(tenantSlug), JSON.stringify(hop));
    else window.sessionStorage.removeItem(hopKey(tenantSlug));
  } catch {
    /* storage blocked: the return waits as before */
  }
}

export function readPaystackHop(tenantSlug: string, now = Date.now()): PaystackHopRecord | null {
  try {
    return parseHop(window.sessionStorage.getItem(hopKey(tenantSlug)), now);
  } catch {
    return null;
  }
}

/** Paystack sent the browser back with this reference: a reload from here asks about it again. */
export function markPaystackHopReturned(tenantSlug: string, reference: string): void {
  const hop = readPaystackHop(tenantSlug);
  if (!hop || hop.reference !== reference) return;
  try {
    window.sessionStorage.setItem(hopKey(tenantSlug), JSON.stringify(markHopReturned(hop, reference)));
  } catch {
    /* storage blocked */
  }
}

/** What Paystack said about the trip's payment ("paid", "in_progress", or null: forget it). */
export function rememberPaystackSaid(tenantSlug: string, reference: string, said: PaystackSaid | null): void {
  const hop = readPaystackHop(tenantSlug);
  if (!hop || hop.reference !== reference || hop.said === said) return;
  try {
    window.sessionStorage.setItem(hopKey(tenantSlug), JSON.stringify(markHopSaid(hop, reference, said)));
  } catch {
    /* storage blocked: this page load still remembers it */
  }
}

export function clearPaystackHop(tenantSlug: string): void {
  try {
    window.sessionStorage.removeItem(hopKey(tenantSlug));
  } catch {
    /* storage blocked: nothing was kept */
  }
}

/** Where a resumed checkout reopens, and what it says there. */
export type ResumedCheckout = {
  step: StepId;
  riderIndex: number;
  /** resumeNotice: why Review & pay reopened after a trip to Paystack (null: a plain reload). */
  notice: string | null;
  /** The same notice without "apply your code again", shown once those codes are back on (noticeNow). */
  noticeOnceCodesBack?: string | null;
  /** The codes the entry had, which the notice asks for again. */
  codesAsked?: { codesApplied: boolean; codeKinds: readonly CodeKind[]; linkCodeOnly: boolean };
  /** The trip to Paystack the checkout carries on remembering (a reload asks about it again). */
  payment: ResumePayment | null;
  /** After a trip to Paystack: what the checkout fills back in (null: a plain reload). */
  fields: ReopenedFields | null;
};

/**
 * Why a kept checkout did not reopen:
 *   lapsed      its hold ended (or the server will not keep it): the entry
 *               is cleared;
 *   changed     it is not the cart held now (a line added, removed or let
 *               go since, on the server or in this tab): cleared;
 *   unanswered  the server did not answer: the entry is kept, try again.
 */
export type RestoreProblem = "lapsed" | "changed" | "unanswered";
export type RestoreResult = { ok: true; checkout: ResumedCheckout } | { ok: false; problem: RestoreProblem };

/**
 * A stored checkout whose holds are still this cart's and still live is
 * restored — the tab adopts its token and the cart gets its lines back —
 * and anything else is cleared. extend_cart_hold is the check: it refuses a
 * lapsed hold (never revives one), a staff or managed cart, and a set of
 * holds that differs from the stored one (cart_mismatch: a stray row the
 * page would not show but the payment would charge), so only an exact, live
 * cart comes back. A reply that says nothing (the network) keeps the entry
 * for the next load.
 *
 * The caller decides first what a trip to Paystack means (useCheckoutReturn
 * asks the server about the payment); this only restores, on Review & pay
 * when there was one, with the caller's notice.
 */
export async function restoreCheckout(input: {
  tenantSlug: string;
  entry: CheckoutResume;
  notice: string | null;
  /** The notice once the entry's codes are back on (default: the notice). */
  noticeOnceCodesBack?: string | null;
  fields?: ReopenedFields | null;
  cartIsEmpty: () => boolean;
  add: (item: Omit<CartItem, "id">) => void;
}): Promise<RestoreResult> {
  const { entry } = input;
  const result = await extendCartHold(supabase, entry.cartToken, entry.bookingIds).catch(() => null);
  if (!result || (!result.ok && (result.reason === "busy" || result.reason === "unknown")))
    return { ok: false, problem: "unanswered" };
  if (!result.ok) {
    clearCheckoutResume(input.tenantSlug);
    return { ok: false, problem: result.reason === "cart_mismatch" ? "changed" : "lapsed" };
  }
  // This tab holds another cart now (the guest added something after
  // leaving the checkout): the kept one cannot come back beside it.
  if (!input.cartIsEmpty()) {
    clearCheckoutResume(input.tenantSlug);
    return { ok: false, problem: "changed" };
  }
  if (!adoptCartToken(entry.cartToken)) return { ok: false, problem: "unanswered" };
  for (const line of entry.lines) input.add({ ...line, metadata: { ...line.metadata } });
  return {
    ok: true,
    checkout: {
      step: entry.payment ? "review" : entry.step,
      riderIndex: entry.riderIndex,
      notice: input.notice,
      noticeOnceCodesBack: input.noticeOnceCodesBack === undefined ? input.notice : input.noticeOnceCodesBack,
      codesAsked: { codesApplied: entry.codesApplied, codeKinds: entry.codeKinds, linkCodeOnly: entry.linkCodeOnly },
      payment: entry.payment,
      fields: entry.payment ? (input.fields ?? null) : null,
    },
  };
}
